import { Injectable } from '@angular/core';
import { DataRepository, ReferenceTableRepository, WorkspaceDatabaseKeys } from '@database/workspace';
import { QueryParamsProvider } from '@domain/routing';
import { DataModel, listToDictionary, ReferenceTableModel } from '@shared';
import { Observable, of } from 'rxjs';
import { first, map, switchMap } from 'rxjs/operators';
import { TableTypeProvider } from './table-type.provider';

@Injectable()
export class SampleTypeProvider {
	public constructor(
		private refTableRepository: ReferenceTableRepository,
		private dataRepository: DataRepository,
		private queryParamsProvider: QueryParamsProvider,
		private tableTypeProvider: TableTypeProvider
	) {}

	public getSampleTypes$(): Observable<{ [key: string]: boolean }> {
		return this.refTableRepository
			.getAll(WorkspaceDatabaseKeys.referenceTables.assaySampleTypeRefTable.toLowerCase())
			.pipe(
				map((refTables: ReferenceTableModel[]) => {
					const sampleTypeCodesDict: { [key: string]: boolean } = {};

					refTables.forEach((refTable: ReferenceTableModel) => {
						sampleTypeCodesDict[refTable[WorkspaceDatabaseKeys.referenceTables.assaySampleTypeCode]] =
							refTable[WorkspaceDatabaseKeys.referenceTables.assaySampleTypeCategory].toLowerCase() ===
							WorkspaceDatabaseKeys.referenceTables.originalCategory;
					});

					return sampleTypeCodesDict;
				})
			);
	}

	public getGroupedByCategorySampleTypes$(): Observable<{ [key: string]: ReferenceTableModel[] }> {
		return this.refTableRepository
			.getAll(WorkspaceDatabaseKeys.referenceTables.assaySampleTypeRefTable.toLowerCase())
			.pipe(
				map((refTables: ReferenceTableModel[]) => {
					if (refTables.length === 0) {
						return {};
					}

					refTables = refTables.map((refTable: ReferenceTableModel) => ({
						...refTable,
						[WorkspaceDatabaseKeys.referenceTables.assaySampleTypeCategory]:
							refTable[WorkspaceDatabaseKeys.referenceTables.assaySampleTypeCategory].toLowerCase()
					}));

					const sampleTypeCodesDict: { [key: string]: ReferenceTableModel[] } = listToDictionary(
						WorkspaceDatabaseKeys.referenceTables.assaySampleTypeCategory,
						refTables
					);

					return sampleTypeCodesDict;
				})
			);
	}

	public isQcSampleEntry$(): Observable<boolean> {
		return this.tableTypeProvider.isSampleTable$(this.queryParamsProvider.activeTableName).pipe(
			switchMap((isSampleTable: boolean) =>
				isSampleTable
					? this.dataRepository
							.getTableEntry$(
								this.queryParamsProvider.activeTableName,
								this.queryParamsProvider.projectNumber,
								Number(this.queryParamsProvider.activeTableId)
							)
							.pipe(
								switchMap((tableEntry: DataModel) =>
									this.getSampleTypes$().pipe(
										map((sampleTypes: { [key: string]: boolean }) =>
											tableEntry ? !sampleTypes[tableEntry?.assay_sample_type_code] : false
										)
									)
								)
							)
					: of(false)
			),
			first()
		);
	}
}
